import cn from 'clsx'
import Group from 'components/utils/Group'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import React, { useCallback, useEffect, useId, useMemo, useState } from 'react'
import styled from 'styled-components'
import ExperienceDetailsAboutLocationMap from '../ExperienceDetailsModal/About/ExperienceDetailsAboutLocationMap'
import TextButton from 'components/Luxkit/Button/TextButton'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { fetchTransferOptions } from 'actions/ExperienceActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import ExperienceTransferMultipleBooking from './ExperienceTransferMultipleBooking'
import ExperienceTransferSingleBooking from './ExperienceTransferSingleBooking'
import { rem } from 'polished'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import { EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW } from 'constants/experience'
import { getAgentHubExperienceType } from 'lib/agentHub/agentHubUtils'
import ImageGallery from 'components/Luxkit/Carousel/ImageGallery'
import Typography from 'components/Luxkit/Typography/Typography'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import CalendarIcon from 'components/Luxkit/Icons/line/LineCalendarIcon'
import ClockFiveIcon from 'components/Luxkit/Icons/line/LineClockFiveIcon'
import ExperienceCancellationText from 'components/Experiences/ExperienceCancellationPolicy/ExperienceCancellationText'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import Divider from 'components/Luxkit/Divider'
import Accordion from 'components/Luxkit/Accordion'
import { MAP_TITLE_TO_EVENT } from 'components/Experiences/ExperienceOfferPage/ExperienceOfferPageContent'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import LoyaltyPoints from 'loyalty/components/LoyaltyPoints'
import { getExperienceDurationString } from 'lib/experiences/experienceUtils'

const Footer = styled.div`
  display: flex;
  width: 100%;
  gap: ${rem(12)};
  justify-content: space-between;

  flex-direction: column;

  >:last-child {
    width: 100%;
  }

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    align-items: center;

    >:last-child {
      width: auto;
    }
  }
`

const FooterPrice = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};

  ${mediaQueryUp.tablet} {
    display: block;
  }
`

const FadingElement = styled.div`
  display: none;
  min-width: ${rem(120)};

  &.visible {
    display: block;
  }

  ${mediaQueryUp.tablet} {
    display: block;
    opacity: 0;
    transition: opacity 0.2s;
    pointer-events: none;

    &.visible {
      display: block;
      opacity: 1;
      pointer-events: auto;
    }
  }
`

const SubmitButtonContainer = styled.div`
  min-width: ${rem(120)};
`

const ImageContainer = styled.div`
  height: ${rem(280)};

  & > * {
    height: 100%;
    width: 100%;
  }
`

export interface TransferBookingModalResolve {
  remove?: boolean;
  transfers?: Array<App.ExperienceTransferView>;
}

interface Props {
  experience: App.ExperienceBookingView;
  defaultTransfer?: App.ExperienceTransferView;
  defaultToAirportTransfer?: App.ExperienceTransferView;
  defaultToHotelTransfer?: App.ExperienceTransferView;
  pointsEarnCalculationRequest?: Array<App.LoyaltyPointsEarnCalculationRequest>;
}

function ExperienceTransferBookingModal(props: Props) {
  const { experience: experienceView, defaultTransfer, defaultToAirportTransfer, defaultToHotelTransfer } = props
  const modalElement = useModalElementContext<{ transfers?: Array<App.ExperienceTransferView>; remove?: boolean }>()
  const [price, setPrice] = useState<{total: number; isFromPrice: boolean}>({ total: 0, isFromPrice: true })
  const formId = useId()
  const trackEvent = useTrackExperienceOfferEvent()
  const experience = experienceView.originalExperience

  const dispatch = useAppDispatch()

  const transferOptions = useAppSelector(state => state.experience.experienceTransferOptions[experienceView.id]?.transfers)

  useEffect(() => {
    dispatch(fetchTransferOptions(experienceView.id))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experienceView.id])

  const onSubmit = useCallback((transfers: Array<App.ExperienceTransferView>) => {
    modalElement.resolve({ transfers })
  }, [modalElement])

  const onRemove = () => {
    modalElement.resolve({ remove: true })
  }

  const productType = useMemo(() => getAgentHubExperienceType(experience), [experience])

  const hasDescription = useMemo(
    () => experience.copy.dealDescription && experience.copy.dealDescription.trim() !== experienceView.title.trim(),
    [experience.copy.dealDescription, experienceView.title],
  )

  const onPriceChange = useCallback((total: number, isFromPrice: boolean) => setPrice({ total, isFromPrice }), [])
  const canRemove = !!(defaultTransfer?.option ?? defaultToAirportTransfer?.option ?? defaultToHotelTransfer?.option)

  const handleThingsToKnowOpen = useCallback((title: string) => {
    if (MAP_TITLE_TO_EVENT[title].open) {
      trackEvent(MAP_TITLE_TO_EVENT[title].open)
    }
  }, [trackEvent])

  const handleThingsToKnowClose = useCallback((title: string) => {
    if (MAP_TITLE_TO_EVENT[title].close) {
      trackEvent(MAP_TITLE_TO_EVENT[title].close)
    }
  }, [trackEvent])

  const pointsEarnCalculationRequest = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [{
    price: price.total,
    loyaltyProductType: 'experience',
  }], [price])

  const durationString = useMemo(() => getExperienceDurationString(experience), [experience])

  return <ModalBase size="L" height="max">
    <ModalHeader title="" isOverlay />
    <ModalBody>
      <ImageContainer>
        <ImageGallery
          size="small"
          images={experience.images}
          showImageTitles={false}
        />
      </ImageContainer>
      <ModalContent>
        <Group direction="vertical" gap={20} tabletGap={24}>
          <Group direction="vertical" gap={8}>
            <Typography wrap="truncate">
              {experienceView.location && <>
                <Caption as="span" variant="large" colour="neutral-one" weight="normal">{experienceView.location}</Caption>{' '}
                <Caption as="span" variant="large" colour="neutral-one" weight="normal">·</Caption>{' '}
              </>}
              <BodyText as="span" variant="small" colour="neutral-two" >{experienceView.primaryCategory}</BodyText>
            </Typography>

            <Heading as="h3" variant="heading3">{experienceView.title}</Heading>

            <BodyTextBlock variant="medium" startIcon={<CalendarIcon aria-hidden="true" />}>
              <BodyText variant="medium">
                <BodyText variant="medium" weight="bold" as="span">Availability: </BodyText> {experience.copy.availability}
              </BodyText>
            </BodyTextBlock>

            <BodyTextBlock variant="medium" startIcon={<ClockFiveIcon aria-hidden="true" />}>
              <BodyText variant="medium">
                <BodyText variant="medium" weight="bold" as="span">Duration:</BodyText> {durationString}
              </BodyText>
            </BodyTextBlock>

            <ExperienceCancellationText
              cancellationInfo={experience.copy.cancellationInfo}
            />
            {hasDescription && <LargeContentToggle maxHeight={80} showMoreText="Read more" showLessText="Read less">
              <MarkdownRender content={experience.copy.dealDescription ?? ''} />
            </LargeContentToggle>}
          </Group>
          <Divider kind="primary" />
          {experience.copy.included?.trim() &&
            <>
              <div>
                <LargeContentToggle maxHeight={80} showMoreText="View more inclusions"
                  showLessText="View less inclusions"
                >
                  <BodyText as="h6" variant="large" weight="bold">Inclusions</BodyText>
                  <MarkdownRender type="compact" content={experience.copy.included} />
                </LargeContentToggle>
              </div>
              <Divider kind="primary" />
            </>
          }
          {!transferOptions && <div>
            <LoadingIndicator inline visible>Finding available transfers...</LoadingIndicator>
          </div>}
          {transferOptions && <>
            {defaultTransfer && <ExperienceTransferSingleBooking
              defaultTransfer={defaultTransfer}
              experience={experienceView}
              transferOptions={transferOptions}
              onPriceChange={onPriceChange}
              onSubmit={onSubmit}
              formId={formId}
            />}
            {!defaultTransfer && <ExperienceTransferMultipleBooking
              defaultToHotelTransfer={defaultToHotelTransfer}
              defaultToAirportTransfer={defaultToAirportTransfer}
              experience={experienceView}
              transferOptions={transferOptions}
              onPriceChange={onPriceChange}
              onSubmit={onSubmit}
              formId={formId}
            />}
          </>}
          <Divider kind="primary" />
          <div>
            <Accordion heading="Location" size="large" hideDivider>
              <ExperienceDetailsAboutLocationMap
                experienceView={experienceView}
              />
            </Accordion>
            {experience.copy.thingsToKnow.map(item => (
              <Accordion
                key={item.title}
                onOpen={() => handleThingsToKnowOpen(item.title)}
                onClose={() => handleThingsToKnowClose(item.title)}
                heading={item.title}
                size="large"
                hideDivider
              >
                <MarkdownRender content={item.content} type="compact" />
              </Accordion>
            ))}
          </div>
        </Group>
      </ModalContent>
    </ModalBody>
    <ModalFooter>
      <Footer>
        <FooterPrice>
          <Group direction="vertical" gap={8}>
            <FadingElement as={Caption} className={cn({ visible: price.isFromPrice })} variant="medium" colour="neutral-two">
              From
            </FadingElement>
            <PriceRowPrice
              size="L"
              price={price.total}
              saleUnit={price.isFromPrice && experienceView.ticketUnitLabel ? experienceView.ticketUnitLabel : 'total'}
              discountPercentage={price.isFromPrice && experienceView.discount >= EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW ? experienceView.discount : undefined}
              discountType="estimate"
            />
            <PriceRowAgentHubCommission
              offerId={experience.id}
              productType={productType}
              offerLocations={[experience.location.name]}
              size="L"
            />
            <LoyaltyPoints
              calculationRequests={pointsEarnCalculationRequest}
              calculationType={price.isFromPrice ? 'estimate' : 'exact'}
              hideStatusCredits
            />
          </Group>
        </FooterPrice>
        <Group direction="vertical" tabletDirection="horizontal" gap={8}>
          <FadingElement
            as={TextButton}
            aria-hidden={!canRemove}
            className={cn({ visible: canRemove })}
            kind="tertiary"
            size="large"
            onClick={onRemove}
          >
            Remove
          </FadingElement>
          <SubmitButtonContainer>
            <TextButton
              kind="primary"
              size="large"
              type="submit"
              fit="full-width"
              form={formId}
            >
              {canRemove ? 'Update' : 'Add'}
            </TextButton>
          </SubmitButtonContainer>
        </Group>
      </Footer>
    </ModalFooter>
  </ModalBase>
}

export default ExperienceTransferBookingModal
